import { RefObject } from 'react';
import {
    IComparisonBoardItem,
} from '../../../../interfaces/comparison-boards/IComparisonBoards';
import Switch from '../../../../shared/switch';
import {
    getComparisonBoardClient1,
    getComparisonBoardClient2,
    getComparisonBoardActiveClientId,
    getComparisonBoardId,
    isComparisonBoardClone,
    isComparisonBoardEnabled,
} from '../utils';

const tableColumns = [
    'Main Account',
    'Clone',
    'Cloned Account',
    'Properties',
    'Active',
];

const yesNoClassName = (value: boolean) =>
    value
        ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700'
        : 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-900/40 dark:text-gray-300 dark:border-gray-700';

const ComparisonBoardsTable = ({
    rows,
    loading,
    loadingMore,
    hasMore,
    updatingIds,
    onLoadMore,
    onChangeStatus,
    scrollContainerRef,
}: {
    rows?: IComparisonBoardItem[];
    loading?: boolean;
    loadingMore?: boolean;
    hasMore?: boolean;
    updatingIds?: string[];
    onLoadMore?: () => void;
    onChangeStatus?: (row: IComparisonBoardItem, enabled: boolean) => void;
    scrollContainerRef?: RefObject<HTMLDivElement>;
}) => {
    const handleScroll = () => {
        if (!scrollContainerRef?.current || !hasMore || loadingMore || loading) return;
        const el = scrollContainerRef.current;
        if (el.scrollHeight <= el.clientHeight) return;
        if (el.scrollTop <= 0) return;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
            onLoadMore?.();
        }
    };

    return (
        <div className='overflow-x-auto border border-1 rounded-xl dark:border-borderColor-darkMode'>
            <div
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className='overflow-y-auto'
                style={{ maxHeight: '650px' }}
            >
                <table className='min-w-full bg-white dark:bg-black dark:text-white min-h-[220px]'>
                    <thead className='sticky top-0 z-10 bg-white dark:bg-black text-left text-sm font-medium text-black dark:text-white'>
                        <tr>
                            {tableColumns.map((column) => (
                                <th key={column} className='px-4 py-3 min-w-[170px]'>
                                    {column}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {loading && (!rows || rows.length === 0)
                            && Array.from({ length: 8 }).map((_, i) => (
                                <tr key={`comparison-board-skeleton-${i}`} className='animate-pulse border-b'>
                                    {Array.from({ length: tableColumns.length }).map((__, j) => (
                                        <td key={`comparison-board-skeleton-cell-${i}-${j}`} className='px-4 py-4'>
                                            <div className='h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4' />
                                        </td>
                                    ))}
                                </tr>
                            ))}

                        {!loading && (!rows || rows.length === 0) && (
                            <tr>
                                <td colSpan={tableColumns.length} className='px-4 py-8 text-center text-gray-500 dark:text-gray-400'>
                                    No comparison boards currently available
                                </td>
                            </tr>
                        )}

                        {rows && rows.length > 0 && rows.map((row, idx) => {
                            const id = getComparisonBoardId(row);
                            const activeClientId = getComparisonBoardActiveClientId(row);
                            const clone = isComparisonBoardClone(row);
                            const enabled = isComparisonBoardEnabled(row);
                            const properties = Array.isArray(row.properties) ? row.properties : [];
                            const isUpdating = activeClientId ? updatingIds?.includes(activeClientId) : false;

                            return (
                                <tr
                                    key={`${id || 'comparison-board'}-${idx}`}
                                    className='hover:bg-gray-50 dark:hover:bg-bgColor-darkMode text-sm border-b'
                                >
                                    <td className='px-4 py-4'>
                                        <p className='max-w-[260px] break-words font-medium'>
                                            {getComparisonBoardClient1(row)}
                                        </p>
                                    </td>
                                    <td className='px-4 py-4'>
                                        <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${yesNoClassName(clone)}`}>
                                            {clone ? 'Yes' : 'No'}
                                        </span>
                                    </td>
                                    <td className='px-4 py-4'>
                                        <p className='max-w-[260px] break-words'>
                                            {getComparisonBoardClient2(row)}
                                        </p>
                                    </td>
                                    <td className='px-4 py-4'>
                                        <div className='min-w-[180px]'>
                                            <p className='font-medium'>
                                                {properties.length} {properties.length === 1 ? 'property' : 'properties'}
                                            </p>
                                            {properties.slice(0, 2).map((property) => (
                                                <p
                                                    key={property.property}
                                                    className='mt-1 max-w-[240px] truncate text-xs text-gray-500 dark:text-gray-400'
                                                    title={property.property}
                                                >
                                                    {property.property}
                                                </p>
                                            ))}
                                        </div>
                                    </td>
                                    <td className='px-4 py-4'>
                                        <div className={isUpdating ? 'opacity-60 pointer-events-none' : ''}>
                                            <Switch
                                                checked={enabled}
                                                afterText={enabled ? 'Yes' : 'No'}
                                                onToggleSwitch={() => onChangeStatus?.(row, !enabled)}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}

                        {loadingMore && rows && rows.length > 0 && (
                            <tr>
                                <td colSpan={tableColumns.length} className='px-4 py-5'>
                                    <div className='flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400'>
                                        <svg className='h-5 w-5 animate-spin text-gray-400' viewBox='0 0 24 24' fill='none'>
                                            <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                                            <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8v4A4 4 0 008 12H4z' />
                                        </svg>
                                        <span>Loading more comparison boards...</span>
                                    </div>
                                </td>
                            </tr>
                        )}

                        {!loading && !loadingMore && rows && rows.length > 0 && hasMore === false && (
                            <tr>
                                <td colSpan={tableColumns.length} className='px-4 py-4 text-center text-xs text-gray-400'>
                                    All comparison boards loaded
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ComparisonBoardsTable;
